import { getImageUrl } from "../helpers/helpers";

export interface ImagePreloadConfig {
  priority: 'critical' | 'high' | 'medium' | 'low';
  sizes: ImageSize[];
  colors?: string[];
  maxConcurrent?: number;
  timeout?: number;
}

export interface ImageSize {
  width: number;
  height?: number;
  purpose: 'thumbnail' | 'display' | 'gallery' | 'original';
}

export interface PreloadResult {
  url: string;
  loaded: boolean;
  cached: boolean;
  loadTime: number;
  error?: string;
}

export class ImagePreloaderService {
  private cache = new Map<string, PreloadResult>();
  private loadingQueue = new Map<string, Promise<PreloadResult>>();
  private concurrentLimit = 6; // Browser typical limit
  private activeLoads = 0;
  private queuedLoads: (() => Promise<PreloadResult>)[] = [];

  // Image size configurations for different use cases
  public static readonly SIZES = {
    THUMBNAIL: { width: 100, height: 100, purpose: 'thumbnail' as const },
    DISPLAY: { width: 600, purpose: 'display' as const },
    GALLERY: { width: 2000, purpose: 'gallery' as const },
  };

  // Priority tiers for systematic loading
  public static readonly PRIORITY_CONFIGS: Record<string, ImagePreloadConfig> = {
    CRITICAL: {
      priority: 'critical',
      sizes: [ImagePreloaderService.SIZES.THUMBNAIL, ImagePreloaderService.SIZES.DISPLAY],
      colors: ['black'], // Default color only
      maxConcurrent: 6,
      timeout: 5000,
    },
    HIGH: {
      priority: 'high', 
      sizes: [ImagePreloaderService.SIZES.THUMBNAIL],
      maxConcurrent: 4,
      timeout: 8000,
    },
    MEDIUM: {
      priority: 'medium',
      sizes: [ImagePreloaderService.SIZES.DISPLAY, ImagePreloaderService.SIZES.GALLERY],
      maxConcurrent: 3,
      timeout: 12000,
    },
    LOW: {
      priority: 'low',
      sizes: [ImagePreloaderService.SIZES.GALLERY],
      maxConcurrent: 2,
      timeout: 15000,
    },
  };

  /**
   * Generate all possible image URLs for preloading
   */
  generateImageUrls(
    images: any[], 
    config: ImagePreloadConfig,
    selectedColor: string = 'black'
  ): string[] {
    const urls: string[] = [];
    const colors = config.colors || [selectedColor, 'all'];

    images.forEach(image => {
      if (!image.node?.url || !image.node?.altText) return;

      // Filter by color as done in Product component
      const altText = image.node.altText.toLowerCase();
      const shouldInclude = colors.some(color => 
        altText.endsWith(` - ${color.toLowerCase()}`) || altText.endsWith(' - all')
      );

      if (shouldInclude) {
        config.sizes.forEach(size => {
          const url = getImageUrl(image.node.url, size.width, size.height);
          urls.push(url);
        });
      }
    });

    return [...new Set(urls)]; // Remove duplicates
  }

  /**
   * Preload a single image with timeout and error handling
   */
  private async preloadSingleImage(url: string, timeout: number = 10000): Promise<PreloadResult> {
    const startTime = Date.now();

    return new Promise((resolve) => {
      // Check cache first
      if (this.cache.has(url)) {
        resolve(this.cache.get(url)!);
        return;
      }

      const img = new Image();
      const timeoutId = setTimeout(() => {
        resolve({
          url,
          loaded: false,
          cached: false,
          loadTime: Date.now() - startTime,
          error: 'Timeout',
        });
      }, timeout);

      img.onload = () => {
        clearTimeout(timeoutId);
        const result: PreloadResult = {
          url,
          loaded: true,
          cached: img.complete && img.naturalWidth > 0,
          loadTime: Date.now() - startTime,
        };
        this.cache.set(url, result);
        resolve(result);
      };

      img.onerror = () => {
        clearTimeout(timeoutId);
        const result: PreloadResult = {
          url,
          loaded: false,
          cached: false,
          loadTime: Date.now() - startTime,
          error: 'Load failed',
        };
        this.cache.set(url, result);
        resolve(result);
      };

      // Set crossOrigin for Shopify CDN
      img.crossOrigin = 'anonymous';
      img.src = url;
    });
  }

  /**
   * Execute queued image loads with concurrency control
   */
  private async executeQueue() {
    while (this.queuedLoads.length > 0 && this.activeLoads < this.concurrentLimit) {
      const loadFn = this.queuedLoads.shift()!;
      this.activeLoads++;
      
      loadFn().finally(() => {
        this.activeLoads--;
        this.executeQueue(); // Process next item
      });
    }
  }

  /**
   * Preload images with priority and concurrency control
   */
  async preloadImages(
    urls: string[], 
    config: ImagePreloadConfig
  ): Promise<PreloadResult[]> {
    const results: Promise<PreloadResult>[] = [];

    urls.forEach(url => {
      // Avoid duplicate loads
      if (this.loadingQueue.has(url)) {
        results.push(this.loadingQueue.get(url)!);
        return;
      }

      const loadPromise = new Promise<PreloadResult>((resolve) => {
        const loadFn = () => this.preloadSingleImage(url, config.timeout);
        
        if (config.priority === 'critical') {
          // Load immediately for critical images
          resolve(loadFn());
        } else {
          // Queue for controlled loading
          this.queuedLoads.push(async () => {
            const result = await loadFn();
            resolve(result);
            return result;
          });
        }
      });

      this.loadingQueue.set(url, loadPromise);
      results.push(loadPromise);
    });

    // Start processing queue
    this.executeQueue();

    return Promise.all(results);
  }

  /**
   * Preload color option thumbnails from Shopify CDN
   */
  async preloadColorOptions(colors: string[]): Promise<PreloadResult[]> {
    const urls = colors.map(color => {
      const colorSlug = color.toLowerCase().replace(/\s+/g, '-');
      return `https://cdn.shopify.com/s/files/1/0732/5909/0212/files/${colorSlug}-new2.png?v=2`;
    });

    return this.preloadImages(urls, ImagePreloaderService.PRIORITY_CONFIGS.HIGH);
  }

  /**
   * Get preload statistics for monitoring
   */
  getStatistics() {
    const total = this.cache.size;
    const loaded = Array.from(this.cache.values()).filter(r => r.loaded).length;
    const cached = Array.from(this.cache.values()).filter(r => r.cached).length;
    const avgLoadTime = Array.from(this.cache.values())
      .reduce((sum, r) => sum + r.loadTime, 0) / Math.max(total, 1);

    return {
      total,
      loaded,
      cached,
      loadingRatio: loaded / Math.max(total, 1),
      cacheRatio: cached / Math.max(total, 1),
      avgLoadTime: Math.round(avgLoadTime),
      activeLoads: this.activeLoads,
      queuedLoads: this.queuedLoads.length,
    };
  }

  /**
   * Clear cache and reset state
   */
  clearCache() {
    this.cache.clear();
    this.loadingQueue.clear();
    this.queuedLoads = [];
    this.activeLoads = 0;
  }
}

export const imagePreloader = new ImagePreloaderService();